// TipTap Editor initialization
import { Editor } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Youtube from '@tiptap/extension-youtube'
import CodeBlock from '@tiptap/extension-code-block'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import Mention from '@tiptap/extension-mention'
import { Suggestion } from '@tiptap/suggestion'

// Store editor instances globally
window.tiptapEditors = {};

// Simple mention suggestion
const mentionSuggestion = {
    items: ({ query }) => {
        // This is a simple implementation - in production you would fetch users from your API
        const users = [
            'admin', 'moderator', 'user1', 'user2', 'editor', 
            'john', 'jane', 'jack', 'jill', 'james'
        ];
        
        return users
            .filter(item => item.toLowerCase().startsWith(query.toLowerCase()))
            .slice(0, 5);
    },
    
    render: () => {
        let popup;
        let selectedIndex = 0;
        
        const onStart = (props) => {
            popup = document.createElement('div');
            popup.className = 'mention-suggestion bg-white dark:bg-gray-700 shadow-lg rounded-md p-1 overflow-hidden';
            
            // Position the popup
            if (props.clientRect) {
                popup.style.position = 'absolute';
                popup.style.left = `${props.clientRect.left}px`;
                popup.style.top = `${props.clientRect.top + props.clientRect.height}px`;
                popup.style.zIndex = 1000;
                popup.style.width = '150px';
            }
            
            document.body.appendChild(popup);
        };
        
        const onUpdate = (props) => {
            popup.innerHTML = '';
            selectedIndex = 0;
            
            props.items.forEach((item, index) => {
                const suggestionItem = document.createElement('button');
                suggestionItem.className = `
                    mention-suggestion-item w-full text-left px-2 py-1 rounded
                    ${index === selectedIndex ? 'bg-blue-100 dark:bg-blue-800' : 'hover:bg-gray-100 dark:hover:bg-gray-600'}
                    text-gray-800 dark:text-gray-200
                `;
                suggestionItem.textContent = `@${item}`;
                suggestionItem.onclick = () => {
                    props.command({ id: item });
                };
                
                popup.appendChild(suggestionItem);
            });
        };
        
        const onKeyDown = (props) => {
            if (props.event.key === 'ArrowDown') {
                selectedIndex = (selectedIndex + 1) % props.items.length;
                props.event.preventDefault();
                onUpdate(props);
            }
            
            if (props.event.key === 'ArrowUp') {
                selectedIndex = (selectedIndex + props.items.length - 1) % props.items.length;
                props.event.preventDefault();
                onUpdate(props);
            }
            
            if (props.event.key === 'Enter') {
                props.command({ id: props.items[selectedIndex] });
                props.event.preventDefault();
            }
        };
        
        const onExit = () => {
            if (popup) {
                popup.remove();
                popup = null;
            }
        };
        
        return { onStart, onUpdate, onKeyDown, onExit };
    }
};

// Initialize TipTap on a specific element
export function initTipTap(editorId) {
    const editorElement = document.getElementById(editorId);
    const contentField = document.getElementById(`${editorId}-content`);
    
    if (!editorElement) return null;
    
    // Create toolbar
    const toolbar = document.createElement('div');
    toolbar.className = 'tiptap-toolbar flex flex-wrap gap-1 p-2 border-b';
    editorElement.parentNode.insertBefore(toolbar, editorElement);
    
    // Create editor content area
    const editorContent = document.createElement('div');
    editorContent.className = 'tiptap-content';
    editorElement.parentNode.replaceChild(editorContent, editorElement);
    
    // Initialize editor
    const editor = new Editor({
        element: editorContent,
        extensions: [
            StarterKit,
            Image,
            Link.configure({
                openOnClick: false,
            }),
            Table.configure({
                resizable: true,
            }),
            TableRow,
            TableCell,
            TableHeader,
            Youtube.configure({
                inline: false,
                width: 640,
                height: 480,
                controls: true,
                nocookie: false,
                modestBranding: true,
            }),
            CodeBlock.configure({
                languageClassPrefix: 'language-',
                HTMLAttributes: {
                    class: 'tiptap-codeblock rounded p-3 bg-gray-100 dark:bg-gray-800 font-mono text-sm overflow-x-auto',
                },
            }),
            HorizontalRule.configure({
                HTMLAttributes: {
                    class: 'tiptap-hr my-6 border-t-2 border-gray-200 dark:border-gray-700',
                },
            }),
            Mention.configure({
                HTMLAttributes: {
                    class: 'tiptap-mention bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-1 py-0.5 rounded',
                },
                suggestion: mentionSuggestion,
            }),
        ],
        content: contentField.value,
        onUpdate: ({ editor }) => {
            contentField.value = editor.getHTML();
        }
    });
    
    // Create toolbar buttons
    createToolbar(toolbar, editor);
    
    // Store editor instance for later access
    window.tiptapEditors[editorId] = editor;
    
    return editor;
}

// Create toolbar buttons
function createToolbar(toolbar, editor) {
    // Button style classes
    const btnClass = 'tiptap-btn p-2';
    const btnActiveClass = 'is-active';
    
    // Helper to create buttons
    function createBtn(icon, title, action) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = btnClass;
        btn.innerHTML = icon;
        btn.title = title;
        btn.addEventListener('click', action);
        toolbar.appendChild(btn);
        return btn;
    }
    
    // Create button groups
    const textGroup = document.createElement('div');
    textGroup.className = 'flex';
    toolbar.appendChild(textGroup);
    
    // Bold
    const boldBtn = createBtn('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path></svg>', 'Bold', () => editor.chain().focus().toggleBold().run());
    textGroup.appendChild(boldBtn);
    
    // Italic
    const italicBtn = createBtn('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="19" y1="4" x2="10" y2="4"></line><line x1="14" y1="20" x2="5" y2="20"></line><line x1="15" y1="4" x2="9" y2="20"></line></svg>', 'Italic', () => editor.chain().focus().toggleItalic().run());
    textGroup.appendChild(italicBtn);
    
    // Heading buttons
    const h1Btn = createBtn('H1', 'Heading 1', () => editor.chain().focus().toggleHeading({ level: 1 }).run());
    textGroup.appendChild(h1Btn);
    
    const h2Btn = createBtn('H2', 'Heading 2', () => editor.chain().focus().toggleHeading({ level: 2 }).run());
    textGroup.appendChild(h2Btn);
    
    const h3Btn = createBtn('H3', 'Heading 3', () => editor.chain().focus().toggleHeading({ level: 3 }).run());
    textGroup.appendChild(h3Btn);
    
    // Add divider
    const divider1 = document.createElement('div');
    divider1.className = 'w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1';
    toolbar.appendChild(divider1);
    
    // List group
    const listGroup = document.createElement('div');
    listGroup.className = 'flex';
    toolbar.appendChild(listGroup);
    
    // Bullet list
    const bulletListBtn = createBtn('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>', 'Bullet List', () => editor.chain().focus().toggleBulletList().run());
    listGroup.appendChild(bulletListBtn);
    
    // Ordered list
    const orderedListBtn = createBtn('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="10" y1="6" x2="21" y2="6"></line><line x1="10" y1="12" x2="21" y2="12"></line><line x1="10" y1="18" x2="21" y2="18"></line><path d="M4 6h1v4"></path><path d="M4 10h2"></path><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"></path></svg>', 'Ordered List', () => editor.chain().focus().toggleOrderedList().run());
    listGroup.appendChild(orderedListBtn);
    
    // Add divider
    const divider2 = document.createElement('div');
    divider2.className = 'w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1';
    toolbar.appendChild(divider2);
    
    // Media group
    const mediaGroup = document.createElement('div');
    mediaGroup.className = 'flex';
    toolbar.appendChild(mediaGroup);
    
    // Link
    const linkBtn = createBtn('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>', 'Link', () => {
        const url = prompt('Enter URL');
        if (url) {
            editor.chain().focus().setLink({ href: url }).run();
        }
    });
    mediaGroup.appendChild(linkBtn);
    
    // Image
    const imageBtn = createBtn('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>', 'Image', () => {
        const url = prompt('Enter image URL');
        if (url) {
            editor.chain().focus().setImage({ src: url }).run();
        }
    });
    mediaGroup.appendChild(imageBtn);
    
    // YouTube
    const youtubeBtn = createBtn('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon></svg>', 'YouTube Video', () => {
        const url = prompt('Enter YouTube URL');
        if (url) {
            editor.chain().focus().setYoutubeVideo({ src: url }).run();
        }
    });
    mediaGroup.appendChild(youtubeBtn);
    
    // Horizontal Rule
    const hrBtn = createBtn('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"></line></svg>', 'Horizontal Rule', () => {
        editor.chain().focus().setHorizontalRule().run();
    });
    mediaGroup.appendChild(hrBtn);
    
    // Add divider
    const divider3 = document.createElement('div');
    divider3.className = 'w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1';
    toolbar.appendChild(divider3);
    
    // Table group
    const tableGroup = document.createElement('div');
    tableGroup.className = 'flex';
    toolbar.appendChild(tableGroup);
    
    // Table
    const tableBtn = createBtn('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="3" y1="15" x2="21" y2="15"></line><line x1="9" y1="3" x2="9" y2="21"></line><line x1="15" y1="3" x2="15" y2="21"></line></svg>', 'Insert Table', () => {
        editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    });
    tableGroup.appendChild(tableBtn);
    
    // Add divider
    const divider4 = document.createElement('div');
    divider4.className = 'w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1';
    toolbar.appendChild(divider4);
    
    // Special group
    const specialGroup = document.createElement('div');
    specialGroup.className = 'flex';
    toolbar.appendChild(specialGroup);
    
    // Code Block
    const codeBlockBtn = createBtn('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>', 'Code Block', () => {
        editor.chain().focus().toggleCodeBlock().run();
    });
    specialGroup.appendChild(codeBlockBtn);
    
    // Mention
    const mentionBtn = createBtn('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="4"></circle><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94"></path></svg>', 'Mention (@)', () => {
        editor.chain().focus().insertContent('@').run();
    });
    specialGroup.appendChild(mentionBtn);
    
    // Source Edit
    const sourceBtn = createBtn('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path></svg>', 'Source Code', () => {
        const content = editor.getHTML();
        
        // Create modal for source editing
        const modal = document.createElement('div');
        modal.className = 'fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50';
        
        // Modal content
        const modalContent = document.createElement('div');
        modalContent.className = 'bg-white dark:bg-gray-800 rounded-lg shadow-xl w-3/4 max-w-4xl max-h-[80vh] flex flex-col';
        
        // Header
        const header = document.createElement('div');
        header.className = 'p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center';
        header.innerHTML = '<h3 class="text-lg font-semibold text-gray-900 dark:text-white">Edit HTML Source</h3>';
        
        // Close button
        const closeBtn = document.createElement('button');
        closeBtn.className = 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300';
        closeBtn.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
        header.appendChild(closeBtn);
        
        // Body
        const body = document.createElement('div');
        body.className = 'p-4 flex-grow overflow-auto';
        
        // Textarea
        const textarea = document.createElement('textarea');
        textarea.className = 'w-full h-full min-h-[300px] p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm';
        textarea.value = content;
        body.appendChild(textarea);
        
        // Footer
        const footer = document.createElement('div');
        footer.className = 'p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end';
        
        // Apply button
        const applyBtn = document.createElement('button');
        applyBtn.className = 'bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded';
        applyBtn.textContent = 'Apply Changes';
        footer.appendChild(applyBtn);
        
        // Assemble modal
        modalContent.appendChild(header);
        modalContent.appendChild(body);
        modalContent.appendChild(footer);
        modal.appendChild(modalContent);
        document.body.appendChild(modal);
        
        // Handle close
        closeBtn.addEventListener('click', () => {
            modal.remove();
        });
        
        // Handle apply
        applyBtn.addEventListener('click', () => {
            editor.commands.setContent(textarea.value);
            modal.remove();
        });
        
        // Close on outside click
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                modal.remove();
            }
        });
        
        // Focus textarea
        textarea.focus();
    });
    specialGroup.appendChild(sourceBtn);
    
    // Clear formatting
    const clearBtn = createBtn('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path></svg>', 'Clear Formatting', () => {
        editor.chain().focus().clearNodes().unsetAllMarks().run();
    });
    specialGroup.appendChild(clearBtn);
    
    // Update active states on editor updates
    editor.on('update', () => {
        boldBtn.className = editor.isActive('bold') ? `${btnClass} ${btnActiveClass}` : btnClass;
        italicBtn.className = editor.isActive('italic') ? `${btnClass} ${btnActiveClass}` : btnClass;
        h1Btn.className = editor.isActive('heading', { level: 1 }) ? `${btnClass} ${btnActiveClass}` : btnClass;
        h2Btn.className = editor.isActive('heading', { level: 2 }) ? `${btnClass} ${btnActiveClass}` : btnClass;
        h3Btn.className = editor.isActive('heading', { level: 3 }) ? `${btnClass} ${btnActiveClass}` : btnClass;
        bulletListBtn.className = editor.isActive('bulletList') ? `${btnClass} ${btnActiveClass}` : btnClass;
        orderedListBtn.className = editor.isActive('orderedList') ? `${btnClass} ${btnActiveClass}` : btnClass;
        linkBtn.className = editor.isActive('link') ? `${btnClass} ${btnActiveClass}` : btnClass;
        codeBlockBtn.className = editor.isActive('codeBlock') ? `${btnClass} ${btnActiveClass}` : btnClass;
    });
}

// Helper functions for external access
export function getTipTapContent(editorId) {
    if (!window.tiptapEditors || !window.tiptapEditors[editorId]) {
        return '';
    }
    return window.tiptapEditors[editorId].getHTML();
}

export function setTipTapContent(editorId, content) {
    if (!window.tiptapEditors || !window.tiptapEditors[editorId]) {
        return;
    }
    window.tiptapEditors[editorId].commands.setContent(content);
}

// Make functions globally available
window.initTipTap = initTipTap;
window.getTipTapContent = getTipTapContent;
window.setTipTapContent = setTipTapContent;

// Initialize all tiptap editors on page load
document.addEventListener('DOMContentLoaded', () => {
    const editors = document.querySelectorAll('.tiptap-editor');
    editors.forEach(editor => {
        initTipTap(editor.id);
    });
}); 